import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Users, Search, Plus, ChevronRight, ChevronLeft, ArrowUpDown,
  Download, Upload, MoreHorizontal, Eye, Trash2, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApprenantsPage = () => {
  const navigate = useNavigate();
  const [apprenants, setApprenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchPrenom, setSearchPrenom] = useState('');
  const [searchNom, setSearchNom] = useState('');
  const [perPage, setPerPage] = useState(100);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortField, setSortField] = useState('nom');
  const [sortDir, setSortDir] = useState('asc');
  const [openMenu, setOpenMenu] = useState(null);
  const [visibleCols, setVisibleCols] = useState(['prenom', 'nom', 'email', 'ville', 'statut', 'entreprise']);
  const [showColPicker, setShowColPicker] = useState(false);

  const ALL_COLUMNS = [
    { id: 'prenom', label: "Prénom de l'apprenant" },
    { id: 'nom', label: "Nom de l'apprenant" },
    { id: 'email', label: 'Email' },
    { id: 'telephone', label: 'Téléphone' },
    { id: 'ville', label: 'Ville' },
    { id: 'statut', label: 'Statut' },
    { id: 'entreprise', label: 'Entreprise' },
    { id: 'fonction', label: 'Fonction' },
    { id: 'nationalite', label: 'Nationalité' },
  ];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/donnees-apprenants`, { credentials: 'include' });
      if (res.ok) setApprenants(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cet apprenant ?")) return;
    await fetch(`${API}/donnees-apprenants/${id}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Apprenant supprimé');
    setOpenMenu(null);
    fetchData();
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/donnees-apprenants/template/excel`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'export_apprenants.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Export téléchargé');
      }
    } catch { toast.error("Erreur d'export"); }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedData.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(paginatedData.map(a => a.apprenant_id)));
  };

  const toggleCol = (id) => {
    setVisibleCols(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  // Filter
  const filtered = apprenants.filter(a => {
    const q = searchQuery.toLowerCase();
    const matchGlobal = !q || `${a.prenom} ${a.nom} ${a.email} ${a.ville}`.toLowerCase().includes(q);
    const matchPrenom = !searchPrenom || (a.prenom || '').toLowerCase().includes(searchPrenom.toLowerCase());
    const matchNom = !searchNom || (a.nom || '').toLowerCase().includes(searchNom.toLowerCase());
    return matchGlobal && matchPrenom && matchNom;
  }).sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase();
    const bVal = (b[sortField] || '').toString().toLowerCase();
    return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginatedData = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <Layout>
      <div className="space-y-5" data-testid="apprenants-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>Données</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-600 font-medium">Apprenants</span>
        </div>

        <h1 className="text-3xl font-black text-slate-900" data-testid="page-title">Apprenants</h1>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 flex-wrap" data-testid="action-buttons">
          <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => navigate('/donnees/apprenants/nouveau')} data-testid="add-apprenant-btn">
            <Plus className="h-4 w-4 mr-2" /> Ajouter un apprenant
          </Button>
          <Button variant="outline" onClick={handleExport} data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" /> Exporter
          </Button>
          <Button variant="outline" onClick={() => navigate('/donnees/apprenants/import')} data-testid="import-btn">
            <Upload className="h-4 w-4 mr-2" /> Importer
          </Button>
        </div>

        {/* Table Card */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="apprenants-table-card">
          {/* Search + Column Picker */}
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Rechercher" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} className="pl-10" data-testid="search-global" />
            </div>
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => setShowColPicker(!showColPicker)} data-testid="col-picker-btn">
                Choisir les colonnes
              </Button>
              {showColPicker && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg py-2 z-30 w-56" data-testid="col-picker-dropdown">
                  {ALL_COLUMNS.map(col => (
                    <button key={col.id} onClick={() => toggleCol(col.id)} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-violet-50 transition-colors">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${visibleCols.includes(col.id) ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`}>
                        {visibleCols.includes(col.id) && <Check className="h-3 w-3 text-white" />}
                      </div>
                      {col.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="apprenants-table">
              <thead>
                <tr className="bg-slate-50/80">
                  <th className="w-12 px-4 py-3">
                    <button onClick={toggleSelectAll} className={`w-5 h-5 rounded border flex items-center justify-center ${selectedIds.size === paginatedData.length && paginatedData.length > 0 ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`} data-testid="select-all-checkbox">
                      {selectedIds.size === paginatedData.length && paginatedData.length > 0 && <Check className="h-3 w-3 text-white" />}
                    </button>
                  </th>
                  {visibleCols.map(colId => {
                    const col = ALL_COLUMNS.find(c => c.id === colId);
                    return (
                      <th key={colId} className="text-left px-4 py-3">
                        <button onClick={() => toggleSort(colId)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700" data-testid={`sort-${colId}`}>
                          {col?.label} <ArrowUpDown className="h-3 w-3" />
                        </button>
                      </th>
                    );
                  })}
                  <th className="w-12 px-4 py-3"></th>
                </tr>
                {/* Column Filters */}
                <tr className="bg-white border-b border-slate-100">
                  <td className="px-4 py-2"></td>
                  {visibleCols.map(colId => (
                    <td key={colId} className="px-4 py-2">
                      {colId === 'prenom' && (
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                          <input className="w-full pl-7 pr-2 py-1 text-xs border border-slate-200 rounded bg-white" placeholder="" value={searchPrenom} onChange={e => { setSearchPrenom(e.target.value); setPage(1); }} data-testid="filter-prenom" />
                        </div>
                      )}
                      {colId === 'nom' && (
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                          <input className="w-full pl-7 pr-2 py-1 text-xs border border-slate-200 rounded bg-white" placeholder="" value={searchNom} onChange={e => { setSearchNom(e.target.value); setPage(1); }} data-testid="filter-nom" />
                        </div>
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-2"></td>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i}><td colSpan={visibleCols.length + 2} className="px-4 py-3"><div className="h-8 bg-slate-100 rounded animate-pulse" /></td></tr>
                  ))
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={visibleCols.length + 2} className="px-4 py-16 text-center">
                      <Users className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                      <p className="text-sm text-slate-400">Aucun apprenant trouvé</p>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map(a => (
                    <tr key={a.apprenant_id} className="hover:bg-violet-50/30 cursor-pointer transition-colors" onClick={() => navigate(`/donnees/apprenants/${a.apprenant_id}`)} data-testid={`apprenant-row-${a.apprenant_id}`}>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <button onClick={() => toggleSelect(a.apprenant_id)} className={`w-5 h-5 rounded border flex items-center justify-center ${selectedIds.has(a.apprenant_id) ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`} data-testid={`checkbox-${a.apprenant_id}`}>
                          {selectedIds.has(a.apprenant_id) && <Check className="h-3 w-3 text-white" />}
                        </button>
                      </td>
                      {visibleCols.map(colId => (
                        <td key={colId} className="px-4 py-3">
                          {colId === 'prenom' && <span className="text-sm text-violet-600 font-medium hover:underline">{a.prenom || ''}</span>}
                          {colId === 'nom' && <span className="text-sm text-violet-600 font-medium hover:underline">{a.nom || ''}</span>}
                          {colId === 'email' && <span className="text-sm text-slate-600">{a.email || ''}</span>}
                          {colId === 'telephone' && <span className="text-sm text-slate-600">{a.telephone || ''}</span>}
                          {colId === 'ville' && <span className="text-sm text-slate-600">{a.ville || ''}</span>}
                          {colId === 'statut' && a.statut && <Badge variant="outline" className="text-xs">{a.statut}</Badge>}
                          {colId === 'entreprise' && <span className="text-sm text-slate-600">{a.entreprise || ''}</span>}
                          {colId === 'fonction' && <span className="text-sm text-slate-600">{a.fonction || ''}</span>}
                          {colId === 'nationalite' && <span className="text-sm text-slate-600">{a.nationalite || ''}</span>}
                        </td>
                      ))}
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <div className="relative">
                          <button onClick={() => setOpenMenu(openMenu === a.apprenant_id ? null : a.apprenant_id)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid={`menu-${a.apprenant_id}`}>
                            <MoreHorizontal className="h-4 w-4 text-slate-400" />
                          </button>
                          {openMenu === a.apprenant_id && (
                            <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 z-20 w-44">
                              <button onClick={() => navigate(`/donnees/apprenants/${a.apprenant_id}`)} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-violet-50 transition-colors">
                                <Eye className="h-4 w-4" /> Voir / Modifier
                              </button>
                              <button onClick={e => handleDelete(a.apprenant_id, e)} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors">
                                <Trash2 className="h-4 w-4" /> Supprimer
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && filtered.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between" data-testid="pagination">
              <div className="flex items-center gap-1">
                {[100, 200, 500].map(n => (
                  <button key={n} onClick={() => { setPerPage(n); setPage(1); }} className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${perPage === n ? 'bg-violet-100 text-violet-700' : 'text-slate-500 hover:bg-slate-100'}`} data-testid={`per-page-${n}`}>
                    {n}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span>Page {page} sur {totalPages} ({filtered.length} éléments)</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 disabled:opacity-30" data-testid="prev-page">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => setPage(p)} className={`w-8 h-8 rounded-lg text-xs font-medium flex items-center justify-center ${page === p ? 'bg-violet-600 text-white' : 'hover:bg-slate-100 text-slate-600'}`} data-testid={`page-${p}`}>
                      {p}
                    </button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 disabled:opacity-30" data-testid="next-page">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ApprenantsPage;
